'use client';
import React, { startTransition, useMemo, useRef } from 'react';
import LazyLoad from 'react-lazyload';
import StickyBox from 'react-sticky-box';
import { debounce } from '@benzinga/utils';
import type { Breadcrumb, LayoutHeaderOptions, LayoutNotification } from '@benzinga/content-manager';
import { isMobile } from '@benzinga/device-utils';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { useDetectStickyHeader, useEffectDidMount, useEventListener } from '@benzinga/hooks';
import classNames from 'classnames';
import { SectionSubtitle } from '../Page/SectionSubtitle';
import { Breadcrumbs } from '../Breadcrumbs';

const LazyLoadComponent = LazyLoad as React.ComponentType<React.PropsWithChildren<{ offset?: number; once?: boolean }>>;

const heading2Class =
  'text-[2.6rem] leading-[3.25rem] font-bold max-sm:text-2xl max-sm:leading-[2.6rem] max-sm:font-extrabold';
const heading3Class =
  'text-[1.8rem] leading-[3.25rem] font-bold text-bzgray-600 max-sm:text-[0.9rem] max-sm:leading-[1.9rem] max-sm:font-extrabold';

export interface LayoutProps {
  alignTitleAboveMain?: boolean;
  breadcrumbs?: Breadcrumb[];
  className?: string;
  description?: string;
  hideSidebar?: boolean;
  layoutAbove?: React.ReactElement | boolean;
  layoutBelow?: React.ReactElement | boolean;
  layoutContentClassName?: string;
  layoutFooter?: React.ReactElement | boolean;
  layoutHeader?: React.ReactElement | boolean;
  layoutHeaderAction?: React.ReactElement;
  layoutHeaderOptions?: LayoutHeaderOptions;
  layoutLeftSidebar?: React.ReactElement | boolean;
  layoutMain: React.ReactElement;
  layoutMainClassName?: string;
  layoutSidebar?: React.ReactElement | boolean;
  /** Max width of the content area (e.g. '1400px'). Defaults to '1400px'. Use width="full" for a wider cap. */
  maxWidth?: string;
  notification?: LayoutNotification;
  sidebarSettings?: LayoutSidebarSettings;
  subtitle?: string;
  title?: string;
  url?: string;
  width?: string;
}

export interface LayoutSidebarSettings {
  offsetTop?: number;
  offsetBottom?: number;
  sticky?: boolean;
  visibleOnMobile?: boolean;
  width?: number;
}

export const Layout: React.FC<LayoutProps> = ({
  alignTitleAboveMain,
  breadcrumbs,
  className,
  description,
  hideSidebar,
  layoutAbove,
  layoutBelow,
  layoutContentClassName,
  layoutFooter,
  layoutHeader,
  layoutHeaderAction,
  layoutHeaderOptions,
  layoutLeftSidebar,
  layoutMain,
  layoutMainClassName,
  layoutSidebar,
  maxWidth = '1400px',
  notification,
  sidebarSettings = {},
  subtitle,
  title,
  url,
  width,
}) => {
  const [isSidebarVisible, setSidebarVisibility] = React.useState(true);

  const layoutClasses =
    width === 'full' ? 'layout-content-container' : 'layout-content-container mx-auto px-2 box-content ';
  const layoutClassesWithWidth = layoutClasses + (width === 'full' ? ' w-screen' : ' layout-container');
  const layoutMaxWidthStyle =
    width === 'full'
      ? { maxWidth: 2000 }
      : { maxWidth: maxWidth?.endsWith('px') ? maxWidth : `${maxWidth ?? '1400'}px` };
  const mainWrapperClasses = classNames(
    'main-content-container',
    layoutLeftSidebar && 'layout-has-left-sidebar',
    width === 'full' ? 'w-full' : 'layout-container mx-auto',
  );

  const { entry, isStickyNavbar } = useDetectStickyHeader();
  const leftSidebarOffsetTop = entry?.boundingClientRect?.height
    ? Math.abs(entry?.boundingClientRect?.height) + 10
    : 132;
  const leftSidebarStickyTop =
    typeof sidebarSettings?.offsetTop === 'number'
      ? sidebarSettings.offsetTop
      : isStickyNavbar
        ? leftSidebarOffsetTop
        : 20;

  const handleSidebarVisibility = React.useCallback(() => {
    if (!isMobile()) {
      startTransition(() => setSidebarVisibility(true));
    } else {
      startTransition(() => setSidebarVisibility(false));
    }
  }, []);

  const debouncedSidebarVisibility = useMemo(() => debounce(handleSidebarVisibility, 50), [handleSidebarVisibility]);

  React.useEffect(() => {
    handleSidebarVisibility();
  }, [handleSidebarVisibility]);

  useEventListener('resize', debouncedSidebarVisibility);

  const isLeftSidebarVisible = useMemo(
    () => layoutLeftSidebar && isSidebarVisible,
    [isSidebarVisible, layoutLeftSidebar],
  );

  const showLayoutAbove =
    layoutAbove || title || subtitle || (breadcrumbs && breadcrumbs.length > 0) || notification || description;

  return (
    <div className={classNames('-mt-px', className)}>
      {layoutHeader && (
        <div className="layout-header flex flex-col w-full items-start relative">
          <LayoutHeader options={layoutHeaderOptions}>{layoutHeader}</LayoutHeader>
        </div>
      )}
      <div
        className={classNames(layoutClassesWithWidth, 'mt-4 mb-6', layoutContentClassName)}
        style={layoutMaxWidthStyle ?? {}}
      >
        {showLayoutAbove && (
          <div
            className="w-full mb-4 max-[800px]:pl-0"
            style={{ paddingLeft: alignTitleAboveMain && isLeftSidebarVisible ? 250 : undefined }}
          >
            <div className="layout-container mx-auto">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-8">
                {title && <LayoutHeadline level={2} text={title} url={url} />}
                {layoutHeaderAction}
              </div>
              {subtitle && (
                <LayoutHeadline
                  className={classNames('mb-4 layout-subtitle', heading3Class)}
                  level={3}
                  text={subtitle}
                />
              )}
              {((breadcrumbs && breadcrumbs.length > 0) || notification) && (
                <div className="inline-flex my-2">
                  {breadcrumbs && breadcrumbs.length > 1 && <Breadcrumbs data={breadcrumbs} />}
                  {notification && (
                    <a
                      className="bg-yellow-400 rounded font-bold italic ml-1.5 px-1"
                      href={notification.link}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {notification.label}
                    </a>
                  )}
                </div>
              )}
              {description ? <SectionSubtitle className="mb-4">{description}</SectionSubtitle> : null}
              {layoutAbove}
            </div>
          </div>
        )}
        <div className={mainWrapperClasses}>
          <div className="flex flex-col min-[800px]:flex-row w-full items-start relative [&>div]:w-full min-[800px]:[&>div]:w-auto">
            {layoutLeftSidebar && (
              <StickyBox
                offsetBottom={sidebarSettings?.offsetBottom ?? 20}
                offsetTop={leftSidebarStickyTop}
                style={{
                  display: 'flex',
                  position: 'sticky',
                  top: leftSidebarStickyTop,
                  transition: 'top 0.2s ease-out',
                }}
              >
                <LayoutLeftSidebar visible={!!isLeftSidebarVisible}>{layoutLeftSidebar}</LayoutLeftSidebar>
              </StickyBox>
            )}
            {sidebarSettings?.visibleOnMobile && layoutLeftSidebar && !isLeftSidebarVisible && (
              <LayoutLeftSidebar sidebarWrapperClass="w-full max-w-full" visible>
                {layoutLeftSidebar}
              </LayoutLeftSidebar>
            )}
            <div
              className={classNames(
                'overflow-x-auto w-full min-w-0 flex flex-grow flex-col justify-between',
                layoutMainClassName,
              )}
            >
              {layoutMain}
            </div>
            {isSidebarVisible && layoutSidebar && !hideSidebar && (
              <LayoutSidebarFrame
                offsetBottom={sidebarSettings?.offsetBottom}
                offsetTop={sidebarSettings?.offsetTop}
                sticky={sidebarSettings?.sticky !== false}
                withLeftSidebar={!!layoutLeftSidebar}
              >
                <LayoutSidebar
                  stretch={sidebarSettings?.sticky === false}
                  visible={isSidebarVisible}
                  width={sidebarSettings?.width}
                >
                  {layoutSidebar}
                </LayoutSidebar>
              </LayoutSidebarFrame>
            )}
            {!isSidebarVisible && sidebarSettings?.visibleOnMobile && (
              <div className="w-[300px] ml-0 min-[800px]:ml-4">
                {layoutSidebar && <LazyLoadComponent once>{layoutSidebar}</LazyLoadComponent>}
              </div>
            )}
          </div>
        </div>
        {layoutBelow && <div className="w-full">{layoutBelow}</div>}
      </div>
      {layoutFooter && (
        <LazyLoadComponent offset={100} once>
          <div className="layout-footer flex flex-col w-full items-start relative">
            <div className="w-full overflow-hidden">
              <div className="layout-container mx-auto">{layoutFooter}</div>
            </div>
          </div>
        </LazyLoadComponent>
      )}
    </div>
  );
};

export const LayoutFlex: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col min-[800px]:flex-row w-full items-start relative [&>div]:w-full min-[800px]:[&>div]:w-auto">
    {children}
  </div>
);

const LayoutHeader: React.FC<React.PropsWithChildren<{ options?: LayoutHeaderOptions }>> = React.memo(
  ({ children, options }) => {
    const [isHeaderFixed, setIsHeaderFixed] = React.useState(false);
    const [blockHeight, setBlockHeight] = React.useState(0);
    const blockRef = useRef<HTMLDivElement>(null);

    const style: React.CSSProperties = {};
    if (options?.background_color) style.backgroundColor = options.background_color;

    const variant = classNames(
      'w-full overflow-hidden',
      options?.full_width ? 'p-0 [&_.layout-container]:max-w-full max-[1300px]:[&_.layout-container]:p-0' : 'py-4',
      isHeaderFixed && 'fixed top-0 left-0 right-0',
    );

    useEffectDidMount(() => {
      if (blockRef.current) setBlockHeight(blockRef.current.clientHeight);
    });

    useEventListener('scroll', () => {
      if (!options?.sticky || isMobile()) return;
      const headerElement = document.querySelector('#navigation-header');
      const headerHeight = headerElement?.clientHeight ?? 132;
      if (window.scrollY >= headerHeight && !isHeaderFixed) {
        if (blockRef.current) setBlockHeight(blockRef.current.clientHeight);
        setIsHeaderFixed(true);
      } else if (window.scrollY < headerHeight && isHeaderFixed) {
        setIsHeaderFixed(false);
      }
    });

    if (!children) return null;

    return (
      <>
        {isHeaderFixed && <div style={{ height: `${blockHeight}px` }} />}
        <div className={classNames(variant, 'z-[2]')} ref={blockRef} style={style}>
          <div className={options?.full_width ? 'w-full' : 'layout-container mx-auto'}>{children}</div>
        </div>
      </>
    );
  },
);

interface LayoutHeadlineProps {
  text: string;
  url?: string;
  level?: number;
  className?: string;
}

const LayoutHeadline: React.FC<LayoutHeadlineProps> = ({ className, level = 2, text, url }) => {
  const sanitizedText = sanitizeHTML(text);
  const HeadElement = level === 3 ? 'h3' : 'h2';
  const headingClass = level === 3 ? heading3Class : heading2Class;

  return (
    <HeadElement className={classNames('layout-title', headingClass, className)}>
      {url ? <a href={url}>{sanitizedText}</a> : sanitizedText}
    </HeadElement>
  );
};

const LayoutSidebarFrame: React.FC<
  React.PropsWithChildren<{
    offsetBottom?: number;
    offsetTop?: number;
    sticky: boolean;
    withLeftSidebar: boolean;
  }>
> = ({ children, offsetBottom, offsetTop, sticky, withLeftSidebar }) => {
  const className = classNames(
    'layout-sidebar-sticky-box hidden md:block',
    withLeftSidebar && '!hidden min-[1100px]:!block',
    !sticky && 'self-stretch',
  );

  if (!sticky) {
    return <div className={className}>{children}</div>;
  }

  return (
    <StickyBox className={className} offsetBottom={offsetBottom ?? 20} offsetTop={offsetTop ?? 20}>
      {children}
    </StickyBox>
  );
};

const LayoutSidebar: React.FC<React.PropsWithChildren<{ stretch?: boolean; width?: number; visible: boolean }>> = ({
  children,
  stretch,
  visible,
  width,
}) =>
  visible ? (
    <div
      className={classNames('layout-sidebar ml-0 min-[800px]:ml-4 shrink-0', stretch && 'h-full')}
      style={{ width: width ?? 300 }}
    >
      {children}
    </div>
  ) : null;

const LayoutLeftSidebar: React.FC<React.PropsWithChildren<{ visible: boolean; sidebarWrapperClass?: string }>> = ({
  children,
  sidebarWrapperClass = '',
  visible,
}) =>
  visible ? (
    <div
      className={classNames(
        'block w-[250px] px-1 max-[799px]:px-[0.725rem] max-[799px]:pb-4 max-[799px]:max-w-full max-[799px]:w-full',
        sidebarWrapperClass,
      )}
    >
      {children}
    </div>
  ) : null;

export const LayoutBox: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = '' }) => (
  <div className={classNames('mb-4 w-full', className)}>{children}</div>
);

export const LayoutRow: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = '' }) =>
  children ? <div className={classNames('flex', className)}>{children}</div> : null;

export const H2: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <h2 className={classNames(heading2Class, className)}>{children}</h2>
);

export const H3: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <h3 className={classNames(heading3Class, className)}>{children}</h3>
);

export default Layout;
